import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getStatusColor } from "../../../../constants/Journalconstants";
import TableComponent from "../../../Comman/TableComponent";
import { ArticleService } from "../../../../services/articleServic";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Check, Eye, EyeOff } from "lucide-react";
import { CLIENT, CLIENT_VENDOR } from "../../../../constants/roles";
import { useSelector } from "react-redux";
import { parseList } from "../../../../utils/parseFields";

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${color.bg} ${color.text}`}
    >
      {status || "N/A"}
    </span>
  );
};

const DetailItem = ({ label, children }) => {
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isCopyable = ["Username", "Password"].includes(label);
  const isPassword = label === "Password";
  const value = children ?? "N/A";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const displayValue =
    isPassword && !showPassword && value !== "N/A" && String(value).length > 0
      ? "••••••••"
      : value;

  return (
    <div className="min-w-0">
      <p
        className={`mb-1 text-xs uppercase tracking-wide ${
          isCopyable ? "font-bold text-black" : "font-medium text-gray-700"
        }`}
      >
        {label}
      </p>

      <div
        className={`flex min-w-0 items-center gap-2 text-sm ${
          isCopyable ? "font-bold text-black" : "font-medium text-gray-800"
        }`}
      >
        <span className="min-w-0 break-words">
          {displayValue}
        </span>

        {isPassword && value !== "N/A" && String(value).length > 0 && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            title={showPassword ? "Hide password" : "Show password"}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-[#521785]"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}

        {isCopyable &&
          value !== "N/A" &&
          String(value).length > 0 && (
            <button
              type="button"
              onClick={handleCopy}
              title={`Copy ${label}`}
              aria-label={`Copy ${label}`}
              className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-[#521785]"
            >
              {copied ? (
                <Check size={16} className="text-green-600" />
              ) : (
                <Copy size={16} className="text-blue-600" />
              )}
            </button>
          )}
      </div>
    </div>
  );
};


const getHistoryColumns = (role) => {
  const columns = [
    {
      label: "ID",
      field: "id",
      width: "5%",
      headerClassName: "px-4",
      className: "px-4",
    },
    {
      label: "Name",
      field: "name",
      headerClassName: "px-4",
      className: "px-4",
      render: (item) => (
        <p>
          {item?.changedByDetails?.name} ({item?.changedByDetails?.role})
        </p>
      ),
    },
    {
      label: "Date",
      field: "date",
      headerClassName: "px-4",
      className: "px-4 whitespace-nowrap",
    },
    {
      label: "Pre. Status",
      headerClassName: "px-4",
      className: "px-4",
      render: (item) => (
        <StatusBadge status={item.previousStatus ?? "-"} />
      ),
    },
    {
      label: "Status",
      headerClassName: "px-4",
      className: "px-4",
      render: (item) => <StatusBadge status={item.newStatus} />,
    },
    {
      label: "Added By",
      headerClassName: "px-4",
      width: "15%",
      className: "px-4 whitespace-normal break-all",
      render: (item) => item.addedBy || "N/A",
    },
  ];

  if (role === CLIENT || role === CLIENT_VENDOR) {
    return columns.filter(
      (column) => !["Name", "Added By"].includes(column.label)
    );
  }

  return columns;
};

const JournalDetailView = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const user = useSelector((state) => state?.auth?.user);

  useEffect(() => {
    if (!articleId) {
      setJournal(null);
      setError("Article ID is required.");
      return;
    }

    let cancelled = false;

    const fetchArticleDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const response =
          await ArticleService.getJournalDetails(articleId);

        const article = response?.data;

        if (!article) {
          throw new Error("Article details were not found.");
        }

        if (!cancelled) {
          setJournal(article);
        }
      } catch (err) {
        if (!cancelled) {
          setJournal(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to fetch article details."
          );
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to fetch article details."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchArticleDetails();

    return () => {
      cancelled = true;
    };
  }, [articleId]);

  if (loading) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <p className="text-sm text-gray-500">
          Loading journal details...
        </p>
      </div>
    );
  }

  if (error || !journal) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <button
          type="button"
          onClick={onBack}
          className="mb-4 text-sm text-[#521785] hover:underline"
        >
          &larr; Back to Journal Details
        </button>

        <p className="text-sm text-red-600">
          {error || "Journal details not found."}
        </p>
      </div>
    );
  }

  // const authors = Array.isArray(journal?.authorName)
  // ? journal.authorName
  // : journal?.authorName
  //   ? [journal.authorName]
  //   : [];

  const authors = parseList(journal?.authorName);

  const lastUpdated = journal.updatedAt
    ? new Date(journal.updatedAt).toLocaleString("en-IN")
    : "N/A";

  const history = (journal.statusHistory || []).map(
    (item) => ({
      ...item,
      name: item.name || item.statusName || item.status || "N/A",
      date: item.createdAt
        ? new Date(item.createdAt).toLocaleString("en-IN")
        : "N/A",
      addedBy:
        item.addedBy ||
        item.changedByDetails?.name ||
        item.updatedByDetails?.name ||
        "N/A",
    })
  );

  return (
    <div className="p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 text-sm text-[#521785] hover:underline"
          >
            &larr; Back to Journal Details
          </button>

          <h1 className="text-lg font-semibold text-gray-800 xl:text-xl">
            { journal.journal?.journalCode} {" "} {journal.journal?.journalName ||
              journal.journal?.journalCode ||
              "Journal Details"}
          </h1>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-[#d3cccc] bg-white">
        <div className="flex flex-col justify-between border-b border-[#d3cccc] bg-[#f6eeee] px-5 py-4 md:flex-row">
          <h2 className="font-semibold text-gray-800">
            Journal Information
          </h2>

          <div className="flex items-center gap-2">
            <span className="text-sm">Status:</span>
            <StatusBadge status={journal.status} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 p-5 sm:grid-cols-2 xl:grid-cols-4">
          <div className="sm:col-span-2 xl:col-span-2">
            <DetailItem label="Journal Name">
              {journal.journal?.link  ? (
                <a
                  href={journal.journal?.link }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline"
                >
                  {journal.journal?.journalName ||
                    journal.journal?.journalCode ||
                    "N/A"}
                </a>
              ) : (
                journal.journal?.journalName || journal.journal?.journalCode || "N/A"
              )}
            </DetailItem>
          </div>
          
          <DetailItem label="Username">{journal.username}</DetailItem>

          <DetailItem label="Password">{journal.password}</DetailItem>
            
          <div className="sm:col-span-2 xl:col-span-2">
          <DetailItem label="Title">
            {journal.journal?.document?.title || "-"}
          </DetailItem>
          </div>

          <div className="sm:col-span-2 xl:col-span-2">
            <DetailItem label="Abstract">
              {journal.journal?.document?.abstract || "-"}
            </DetailItem>
          </div>  

          <DetailItem label="Quartile">
            {journal.journal?.quartile || journal.quartile}
          </DetailItem>

          <DetailItem label="Submission Date">
            {journal.submissionDate
              ? new Date(journal.submissionDate).toLocaleDateString("en-IN")
              : "N/A"}
          </DetailItem> 

          {user?.role !== CLIENT_VENDOR && user?.role !== CLIENT && (
          <DetailItem label="Added By">
            {journal.addedByDetails?.name
              ? `${journal.addedByDetails.name} (${journal.addedByDetails.role || "N/A"})`
              : "N/A"}
          </DetailItem>
          )}

          <DetailItem label="Last Updated">{lastUpdated}</DetailItem>
          
        </div>

        <div className="grid grid-cols-1 gap-6 border-t border-[#d3cccc] px-5 py-5 lg:grid-cols-2">
          <DetailItem label="Authors">
            {authors.length ? authors.join(", ") : "N/A"}
          </DetailItem>

          <DetailItem label="Description">
            <p className="whitespace-pre-wrap leading-6 bg-gray-200 p-1 md:px-2 rounded-md">
              {journal.description || "N/A"}
            </p>
          </DetailItem>
        </div>

        <div className="border-t border-[#d3cccc]">
          <div className="px-5 py-4">
            <h2 className="font-bold text-black">
              Status History
            </h2>
            <p className="mt-1 text-xs text-gray-800">
              All previous status updates for this article.
            </p>
          </div>

          <div className="space-y-3 border-t border-[#d3cccc] bg-gray-50 p-3 lg:hidden">
            {history.length > 0 ? (
              history.map((item, index) => (
                <div
                  key={item.id ?? index}
                  className="rounded-lg border border-[#d3cccc] bg-white p-4 shadow-sm"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      History #{item.id ?? index + 1}
                    </span>

                    {/* <StatusBadge status={item.newStatus} /> */}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {/* Name: hidden for CLIENT and CLIENT_VENDOR */}
                    {user?.role !== CLIENT &&
                      user?.role !== CLIENT_VENDOR && (
                        <div className="min-w-0">
                          <p className="mb-1 text-xs font-medium text-gray-500">
                            Name
                          </p>
                          <p className="break-words text-sm font-medium text-gray-800">
                            {item?.changedByDetails?.name || "N/A"}
                            {item?.changedByDetails?.role
                              ? ` (${item.changedByDetails.role})`
                              : ""}
                          </p>
                        </div>
                      )}

                    <div className="min-w-0">
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        Date
                      </p>
                      <p className="break-words text-sm text-gray-800">
                        {item.date || "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        Previous Status
                      </p>
                      <StatusBadge status={item.previousStatus ?? "-"} />
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        Status
                      </p>
                      <StatusBadge status={item.newStatus} />
                    </div>

                    {/* Added By: hidden for CLIENT and CLIENT_VENDOR */}
                    {user?.role !== CLIENT &&
                      user?.role !== CLIENT_VENDOR && (
                        <div className="min-w-0 sm:col-span-2">
                          <p className="mb-1 text-xs font-medium text-gray-500">
                            Added By
                          </p>
                          <p className="break-words text-sm text-gray-800">
                            {item.addedBy || "N/A"}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-lg border border-[#d3cccc] bg-white p-6 text-center text-sm text-gray-500">
                No status history found.
              </div>
            )}
          </div>

          <div className="hidden overflow-x-auto border-t border-[#d3cccc] lg:block">
            <TableComponent
              columns={getHistoryColumns(user?.role)}
              data={history}
              headerBg="bg-[#f6eeee]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalDetailView;
